// SpaceContentEditModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../../util/api";

// MUI
import {
    Box,
    Button,
    Chip,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { notify } from "../../../../../util/toast";

import { ds } from "../../../../../styles/designSystem";
import AppAutocomplete from "../../../../../component/common/AppAutocomplete";
import { keyVisualApi } from "../../api/keyVisualApi";
import FormModal from "../../../../../component/common/FormModal";
import ActionButton from "../../../../../component/common/ActionButton";

export default function SpaceContentEditModal({ item, onClose, onSave }) {
    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    const [eventName, setEventName] = useState(item?.eventName || "");
    const [projectName, setProjectName] = useState(item?.projectName || "");
    const [location, setLocation] = useState(item?.location || "");
    const [locationType, setLocationType] = useState(item?.locationType || "");

    const [keywords, setKeywords] = useState([]);
    const [themes, setThemes] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);

    const [selectedKeywords, setSelectedKeywords] = useState(Array.isArray(item?.keywords) ? item.keywords : []);
    const [selectedThemes, setSelectedThemes] = useState(Array.isArray(item?.themes) ? item.themes : []);

    const [keywordInput, setKeywordInput] = useState("");
    const [themeInput, setThemeInput] = useState("");

    const [saving, setSaving] = useState(false);

    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => (mountedRef.current = false);
    }, []);

    const normalize = useCallback((s) => String(s ?? "").trim(), []);

    const selectedKeywordSet = useMemo(() => new Set(selectedKeywords.map(normalize)), [normalize, selectedKeywords]);
    const selectedThemeSet = useMemo(() => new Set(selectedThemes.map(normalize)), [normalize, selectedThemes]);

    useEffect(() => {
        (async () => {
            try {
                const [kData, tData, pData] = await Promise.all([
                    api.get(`${API_BASE}/space-content/keywords`),
                    api.get(`${API_BASE}/space-content/themes`),
                    keyVisualApi.listProjectNames(),
                ]);

                if (!mountedRef.current) return;
                setKeywords(Array.isArray(kData) ? kData : (kData?.data || []));
                setThemes(Array.isArray(tData) ? tData : (tData?.data || []));
                setProjectOptions(Array.isArray(pData) ? pData : (pData?.data || []));
            } catch (e) {
                console.error(e);
                notify("키워드/테마/프로젝트 목록을 불러오지 못했습니다.", "error");
            }
        })();
    }, [API_BASE]);

    const toggleValue = useCallback((value, list, setList) => {
        const v = normalize(value);
        if (!v) return;

        setList((prev) => {
            const prevNorm = prev.map(normalize);
            const idx = prevNorm.indexOf(v);
            if (idx >= 0) return prev.filter((_, i) => i !== idx);
            return [...prev, v];
        });
    }, [normalize]);

    const addMulti = useCallback((raw, setSelected, selectedSet, label) => {
        const text = String(raw ?? "");
        const parts = text
            .split(/[\s,]+/)
            .map((x) => normalize(x))
            .filter(Boolean);

        // 길이 제한 없음 — 한 글자 키워드도 등록한다(빈 문자열은 위 filter(Boolean) 이 이미 걸렀다).
        const uniq = [];
        for (const p of parts) {
            if (!selectedSet.has(p)) uniq.push(p);
        }

        if (uniq.length === 0) {
            notify(`${label}를 추가할 수 없습니다. (모두 중복)`, "warning");
            return;
        }

        setSelected((prev) => [...prev, ...uniq]);
        notify(`${label} ${uniq.length}개 추가됨`, "success");
    }, [normalize]);

    const handleKeywordKeyDown = useCallback((e) => {
        // 한글 조합 중 Enter 는 '조합 확정' 이지 '입력 완료' 가 아니다 —
        // 거르지 않으면 "한글" 입력 후 Enter 에서 "한", "한글" 두 개가 등록된다.
        if (e.nativeEvent?.isComposing) return;
        if (e.key !== "Enter" && e.key !== ",") return;
        e.preventDefault();
        const text = normalize(keywordInput);
        if (!text) {
            setKeywordInput("");
            return;
        }
        addMulti(text, setSelectedKeywords, selectedKeywordSet, "키워드");
        setKeywordInput("");
    }, [addMulti, keywordInput, normalize, selectedKeywordSet]);

    const handleThemeKeyDown = useCallback((e) => {
        if (e.nativeEvent?.isComposing) return;   // 사유는 handleKeywordKeyDown 주석 참고
        if (e.key !== "Enter" && e.key !== ",") return;
        e.preventDefault();
        const text = normalize(themeInput);
        if (!text) {
            setThemeInput("");
            return;
        }
        addMulti(text, setSelectedThemes, selectedThemeSet, "행사 성격");
        setThemeInput("");
    }, [addMulti, normalize, selectedThemeSet, themeInput]);

    const handleSave = useCallback(async () => {
        if (saving) return;

        const payload = {
            eventName: normalize(eventName),
            projectName: normalize(projectName),
            location: normalize(location),
            locationType: normalize(locationType),
            keywords: selectedKeywords.map(normalize).filter(Boolean),
            themes: selectedThemes.map(normalize).filter(Boolean),
        };

        if (!payload.eventName) return notify("행사명을 입력해주세요.", "warning");
        if (!payload.projectName) return notify("프로젝트명을 입력해주세요.", "warning");
        if (!payload.location) return notify("장소명을 입력해주세요.", "warning");
        if (!payload.locationType) return notify("장소 유형(실내/실외)을 선택해주세요.", "warning");
        if (payload.keywords.length === 0) return notify("키워드를 하나 이상 입력해주세요.", "warning");
        if (payload.themes.length === 0) return notify("행사 성격을 하나 이상 입력해주세요.", "warning");

        try {
            setSaving(true);
            // 백엔드는 ResponseEntity<Void> — 빈 응답. 따라서 원본 item + payload + 서버 응답을 머지해
            // 목록 setData 가 id 로 매칭 가능한 완전한 객체를 만들어 전달한다.
            const serverData = await api.post(`${API_BASE}/space-content/edit/${item.id}`, payload);
            const serverItem = serverData && typeof serverData === "object" ? serverData : null;
            const updated = {
                ...(item || {}),
                ...(serverItem || {}),
                id: serverItem?.id ?? item.id,
                ...payload,
            };
            notify("수정 완료", "success");
            onSave?.(updated);
        } catch (e) {
            console.error(e);
            notify("수정 실패", "error");
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    }, [
        API_BASE,
        eventName,
        item,
        location,
        locationType,
        normalize,
        onSave,
        projectName,
        saving,
        selectedKeywords,
        selectedThemes,
    ]);

    return (
        <FormModal
            open={Boolean(item)}
            onClose={onClose}
            title="공간 콘텐츠 수정"
            subtitle="Enter/쉼표로 추가 · 클릭으로 토글"
            maxWidth="md"
            onSave={handleSave}
            saving={saving}
        >
            <Stack spacing={2}>
                {/* 행사명 */}
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        행사명
                    </Typography>
                    <TextField
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={saving}
                        placeholder="행사명"
                        sx={ds.components.input.outlined}
                    />
                </Box>

                {/* 프로젝트명 */}
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        프로젝트명
                    </Typography>
                    <AppAutocomplete
                        freeSolo
                        options={projectOptions}
                        value={projectName}
                        inputValue={projectName}
                        onChange={(v) => setProjectName(v == null ? "" : String(v))}
                        onInputChange={(v) => setProjectName(v)}
                        disabled={saving}
                        placeholder="기존 프로젝트 선택 또는 새 이름 입력"
                        variant="outlined" disableUnderline={false}
                        textFieldSx={ds.components.input.outlined}
                    />
                </Box>

                {/* 장소 유형 */}
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        장소 유형
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {["실내", "실외"].map((type) => {
                            const active = normalize(locationType) === type;
                            return (
                                <ActionButton
                                    key={type}
                                    variant={active ? "primary" : "secondary"}
                                    startIcon={false}
                                    onClick={() => setLocationType(type)}
                                    disabled={saving}
                                    sx={ds.components.external.pillButton}
                                    label={type}
                                />
                            );
                        })}
                    </Stack>
                </Box>

                {/* 장소명 */}
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        장소명
                    </Typography>
                    <TextField
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={saving}
                        placeholder="장소명"
                        sx={ds.components.input.outlined}
                    />
                </Box>

                {/* 키워드 */}
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        키워드
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {selectedKeywords.map((k, i) => (
                            <Chip
                                key={`${k}-${i}`}
                                label={k}
                                clickable
                                onClick={() => !saving && toggleValue(k, selectedKeywords, setSelectedKeywords)}
                                sx={ds.components.external.chipActive}
                            />
                        ))}
                        {selectedKeywords.length === 0 && (
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary" }}>
                                선택된 키워드가 없습니다.
                            </Typography>
                        )}
                    </Stack>

                    <TextField
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={handleKeywordKeyDown}
                        size="small"
                        fullWidth
                        disabled={saving}
                        placeholder="키워드 입력 후 Enter 또는 , (여러 개 가능)"
                        sx={ds.components.input.outlined}
                    />

                    {Array.isArray(keywords) && keywords.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                            {keywords.map((k, i) => {
                                const nk = normalize(k);
                                const active = selectedKeywordSet.has(nk);
                                return (
                                    <Chip
                                        key={`${k}-${i}`}
                                        label={k}
                                        clickable
                                        onClick={() => !saving && toggleValue(k, selectedKeywords, setSelectedKeywords)}
                                        sx={active ? ds.components.external.chipActive : ds.components.external.chip}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </Box>

                {/* 행사 성격 */}
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        행사 성격
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {selectedThemes.map((t, i) => (
                            <Chip
                                key={`${t}-${i}`}
                                label={t}
                                clickable
                                onClick={() => !saving && toggleValue(t, selectedThemes, setSelectedThemes)}
                                sx={ds.components.external.chipSuccess}
                            />
                        ))}
                        {selectedThemes.length === 0 && (
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary" }}>
                                선택된 행사 성격이 없습니다.
                            </Typography>
                        )}
                    </Stack>

                    <TextField
                        value={themeInput}
                        onChange={(e) => setThemeInput(e.target.value)}
                        onKeyDown={handleThemeKeyDown}
                        size="small"
                        fullWidth
                        disabled={saving}
                        placeholder="행사 성격 입력 후 Enter 또는 , (여러 개 가능)"
                        sx={ds.components.input.outlined}
                    />

                    {Array.isArray(themes) && themes.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                            {themes.map((t, i) => {
                                const nt = normalize(t);
                                const active = selectedThemeSet.has(nt);
                                return (
                                    <Chip
                                        key={`${t}-${i}`}
                                        label={t}
                                        clickable
                                        onClick={() => !saving && toggleValue(t, selectedThemes, setSelectedThemes)}
                                        sx={active ? ds.components.external.chipSuccess : ds.components.external.chip}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </Stack>
        </FormModal>
    );
}
